import { useMemo, useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { SearchSelectOption } from "@/components/SearchSelect";
import { matchesQuery } from "@/lib/picker-options";
import { cn } from "@/lib/utils";

export interface SearchMultiSelectProps {
  /** Accessible name of the trigger, e.g. "Department". */
  label: string;
  /** The picked values; empty means everything. */
  values: string[];
  onChange: (values: string[]) => void;
  options: SearchSelectOption[];
  /** The trigger and first choice when nothing is picked, e.g. All departments. */
  allLabel: string;
  /** For the trigger when several are picked: "3 departments". */
  noun: { one: string; many: string };
  searchPlaceholder?: string;
  /** Trigger width and height. */
  className?: string;
}

/**
 * Pick any number of items from a list the page has already loaded
 * (FEAT-2026-030, KPTM UAT: several campuses, departments and sections at
 * once). The filter counterpart of `SearchSelect`: same search and option
 * layout, but each click ticks or unticks and the list stays open. Nothing
 * picked means no filter, and the All choice clears the picks.
 */
export function SearchMultiSelect({
  label,
  values,
  onChange,
  options,
  allLabel,
  noun,
  searchPlaceholder = "Type to search…",
  className,
}: SearchMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const picked = useMemo(() => new Set(values), [values]);
  const searching = query.trim().length > 0;
  const shown = useMemo(() => options.filter((o) => matchesQuery(o, query)), [options, query]);
  const pickedOptions = options.filter((o) => picked.has(o.value));
  const only = values.length === 1 ? pickedOptions[0] : undefined;

  const toggle = (value: string) =>
    onChange(picked.has(value) ? values.filter((v) => v !== value) : [...values, value]);

  return (
    // Modal, so the list scrolls and takes focus inside an open Dialog.
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={label}
          title={pickedOptions.length ? pickedOptions.map((o) => o.label).join(", ") : undefined}
          // `flex`, not the button's inline-flex, so it sits under its label.
          className={cn("flex w-full justify-between px-3 font-normal", className)}
        >
          <span className="truncate">
            {values.length === 0 ? (
              allLabel
            ) : only ? (
              <>
                {only.label}
                {only.context && <span className="text-muted-foreground"> · {only.context}</span>}
              </>
            ) : (
              `${values.length} ${values.length === 1 ? noun.one : noun.many}`
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(26rem,calc(100vw-2rem))] min-w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandGroup>
              {!searching && (
                <CommandItem value="__all__" onSelect={() => onChange([])}>
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", values.length ? "opacity-0" : "opacity-100")} />
                  {allLabel}
                </CommandItem>
              )}
              {shown.map((o) => {
                const checked = picked.has(o.value);
                return (
                  <CommandItem
                    key={o.value}
                    value={o.value}
                    onSelect={() => toggle(o.value)}
                    data-checked={checked}
                    className="group items-start"
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "mr-2 mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary",
                        // On the highlighted row the box takes the row's colours, or blue vanishes on blue.
                        "group-data-[selected=true]:border-current",
                        checked
                          ? "bg-primary text-primary-foreground group-data-[selected=true]:bg-background group-data-[selected=true]:text-primary"
                          : "[&_svg]:invisible",
                      )}
                    >
                      <Check className="h-3 w-3" />
                    </span>
                    <span className="min-w-0">
                      <span className="block">{o.label}</span>
                      {(o.context || o.note) && (
                        // Faded from the row's own colour so it stays readable when highlighted.
                        <span className="block text-xs opacity-70">
                          {[o.context, o.note].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {searching && shown.length === 0 && (
              <div className="py-3 text-center text-sm text-muted-foreground">{`Nothing matches "${query.trim()}".`}</div>
            )}
          </CommandList>
          {values.length > 0 && (
            <div className="flex items-center justify-between border-t px-3 py-1.5 text-xs text-muted-foreground">
              <span>{values.length} selected</span>
              <Button type="button" variant="ghost" size="sm" className="h-7 px-2" onClick={() => onChange([])}>
                Clear
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
